import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';

// material-ui
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';

// project imports
import AuthWrapper from './AuthWrapper';
import mwLogo from '../../assets/images/mw-logo.png';
import { resetPassword } from '../../utils/api';

export default function AuthResetPassword({ isDemo = false }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('secondary');

  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const hasValidParams = Boolean(email && token);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMessageType('secondary');

    if (!email || !token) {
      setMessage('Invalid password reset link.');
      setMessageType('error.main');
      return;
    }

    if (password !== confirmPassword) {
      setMessage('Passwords do not match.');
      setMessageType('error.main');
      return;
    }

    setLoading(true);

    try {
      const data = await resetPassword({
        email,
        token,
        password,
        password_confirmation: confirmPassword,
      });

      if (data.status === 1) {
        setMessage('Password has been reset successfully! Redirecting to login...');
        setMessageType('success.main');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setMessage(data.msg || data.message || 'Something went wrong.');
        setMessageType('error.main');
      }
    } catch (err) {
      setMessage(err?.response?.data?.message || 'Server error. Please try again later.');
      setMessageType('error.main');
    }

    setLoading(false);
  };

  return (
    <AuthWrapper>
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={8} md={6} lg={4}>
          <Stack spacing={3} alignItems="center">
            {/* Logo + Heading */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h4" fontWeight="bold">
                Reset Password
              </Typography>
              <img src={mwLogo} alt="MW Logo" style={{ width: 35, height: 35 }} />
            </Stack>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              <Stack spacing={2}>
                {!hasValidParams && (
                  <Typography variant="body2" color="error.main" textAlign="center">
                    This reset link is missing the required email or token.
                  </Typography>
                )}

                <TextField
                  label="New Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  required
                  disabled={!hasValidParams || loading}
                />

                <TextField
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  required
                  disabled={!hasValidParams || loading}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!hasValidParams || loading}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>

                {message && (
                  <Typography variant="body2" color={messageType} textAlign="center">
                    {message}
                  </Typography>
                )}

                <Typography
                  component={RouterLink}
                  to="/login"
                  variant="body2"
                  sx={{ textDecoration: 'none', mt: 1 }}
                  color="primary"
                  textAlign="center"
                >
                  Back to Login
                </Typography>
              </Stack>
            </form>
          </Stack>
        </Grid>
      </Grid>
    </AuthWrapper>
  );
}

AuthResetPassword.propTypes = {
  isDemo: PropTypes.bool
};
